import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useLocation } from "react-router-dom";

import ScrollToTop from "components/ScrollToTop";
import RouteHead from "components/RouteHead";
import ErrorBoundary from "components/ErrorBoundary";

// Componentes de Layout y Auth
import ProtectedRoute from "./lib/ProtectedRoute";
import TenantLayout from "./components/layouts/TenantLayout";
import MatchBotLoader from "./components/ui/MatchBotLoader";

// Cada página se carga como un chunk independiente. Suspense muestra la misma
// experiencia de marca tanto en mobile como en desktop durante esa espera real.
// Las páginas públicas prerenderizadas (scripts/prerender.mjs) tienen su importador en UN solo lugar: lo usan
// `lazy` y `preloadPublicRoute`. Antes de hidratar hay que tener el chunk de la ruta actual cargado; si no, el
// límite de Suspense llega sin hidratar a la primera actualización de contexto (la sesión de AuthProvider) y React
// lo descarta y lo vuelve a renderizar en el cliente (error #421). Con red lenta pasaba en todas las rutas.
export const PUBLIC_PAGE_IMPORTERS = {
  "/": () => import("./pages/landing"),
  "/producto": () => import("./pages/producto"),
  "/como-funciona": () => import("./pages/como-funciona"),
  "/seguridad": () => import("./pages/seguridad"),
  "/cuanto-cuesta-whatsapp-api": () => import("./pages/cuanto-cuesta-whatsapp-api"),
  "/como-elegir": () => import("./pages/como-elegir"),
  "/precios": () => import("./pages/precios"),
  "/terminos": () => import("./pages/legal/Terminos"),
  "/privacidad": () => import("./pages/legal/Privacidad"),
  "/reembolsos": () => import("./pages/legal/Reembolsos"),
};

/** Carga el chunk de la página pública de `pathname` (o nada si la ruta no es una de ellas). Nunca rechaza. */
export function preloadPublicRoute(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";
  const importer = PUBLIC_PAGE_IMPORTERS[path];
  return importer ? importer().then(() => undefined, () => undefined) : Promise.resolve();
}

const LandingPage = lazy(PUBLIC_PAGE_IMPORTERS["/"]);
const LoginPage = lazy(() => import("./pages/login"));
const TenantRegistration = lazy(() => import("./pages/tenant-registration"));
const PasswordResetPage = lazy(() => import("./pages/password-reset"));
const ForgotPasswordPage = lazy(() => import("./pages/forgot-password"));
const TeamInvitationPage = lazy(() => import("./pages/team-invitation"));
const FacebookCallback = lazy(() => import("./pages/oauth/FacebookCallback"));
const InstagramCallback = lazy(() => import("./pages/oauth/InstagramCallback"));
const PricingPage = lazy(PUBLIC_PAGE_IMPORTERS["/precios"]);
const SeguridadPage = lazy(PUBLIC_PAGE_IMPORTERS["/seguridad"]);
const ProductoPage = lazy(PUBLIC_PAGE_IMPORTERS["/producto"]);
const ComoFuncionaPage = lazy(PUBLIC_PAGE_IMPORTERS["/como-funciona"]);
const CuantoCuestaPage = lazy(PUBLIC_PAGE_IMPORTERS["/cuanto-cuesta-whatsapp-api"]);
const ComoElegirPage = lazy(PUBLIC_PAGE_IMPORTERS["/como-elegir"]);
const Terminos = lazy(PUBLIC_PAGE_IMPORTERS["/terminos"]);
const Privacidad = lazy(PUBLIC_PAGE_IMPORTERS["/privacidad"]);
const Reembolsos = lazy(PUBLIC_PAGE_IMPORTERS["/reembolsos"]);
const NOT_FOUND_IMPORTER = () => import("./pages/NotFound");
const NotFound = lazy(NOT_FOUND_IMPORTER);

/** Precarga el chunk de la página 404 (build/404.html se hidrata sobre ella; ver preloadPublicRoute). Nunca rechaza. */
export function preloadNotFound() {
  return NOT_FOUND_IMPORTER().then(() => undefined, () => undefined);
}

const TenantDashboard = lazy(() => import("./pages/tenant-dashboard"));
const ChannelSetup = lazy(() => import("./pages/channel-setup"));
const FlowBuilder = lazy(() => import("./pages/flow-builder"));
const TemplateBlueprintsPage = lazy(() => import("./pages/template-blueprints"));
const MessagesLog = lazy(() => import("./pages/messages-log"));
const AgentInboxPage = lazy(() => import("./pages/agent-inbox"));
const ContactsPage = lazy(() => import("./pages/contacts"));
const CampaignsPage = lazy(() => import("./pages/campaigns"));
const AiAssistantPage = lazy(() => import("./pages/ai-assistant"));
const BillingPage = lazy(() => import("./pages/billing"));
const TeamPage = lazy(() => import("./pages/team"));
const TagsPage = lazy(() => import("./pages/tags"));
const ProfilePage = lazy(() => import("./pages/profile"));
const TenantIntegrationsPage = lazy(() => import("./pages/integrations"));
const AdminPanel = lazy(() => import("./pages/admin"));
const AdminIntegrationsPage = lazy(() => import("./pages/admin/integrations"));
const AdminCapabilitiesPage = lazy(() => import("./pages/admin/capabilities"));

const LegacyRedirect = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

export const legacyAppRoutes = {
  "/tenant-dashboard": "/app/overview",
  "/agent-inbox": "/app/inbox",
  "/contacts": "/app/contacts",
  "/campaigns": "/app/campaigns",
  "/ai-assistant": "/app/assistant",
  "/flow-builder": "/app/automations",
  "/templates": "/app/templates",
  "/messages-log": "/app/messages",
  "/channel-setup": "/app/channels",
  "/team": "/app/team",
  "/billing": "/app/billing",
  "/admin": "/app/admin",
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouteHead />
        <Suspense fallback={<MatchBotLoader />}>
        <RouterRoutes>
          
          {/* --- RUTAS PÚBLICAS --- */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/producto" element={<ProductoPage />} />
          <Route path="/como-funciona" element={<ComoFuncionaPage />} />
          <Route path="/seguridad" element={<SeguridadPage />} />
          <Route path="/cuanto-cuesta-whatsapp-api" element={<CuantoCuestaPage />} />
          <Route path="/como-elegir" element={<ComoElegirPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<TenantRegistration />} />
          <Route path="/tenant-registration" element={<Navigate to="/register" replace />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<PasswordResetPage />} />
          <Route path="/invitacion" element={<TeamInvitationPage />} />
          <Route path="/oauth/facebook/callback" element={<FacebookCallback />} />
          <Route path="/oauth/instagram/callback" element={<InstagramCallback />} />

          {/* Precios (público) + páginas legales (requeridas por la pasarela de pago) */}
          <Route path="/precios" element={<PricingPage />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/reembolsos" element={<Reembolsos />} />

          {/* --- RUTAS PROTEGIDAS (LAYOUT MAESTRO) --- */}
          {/* 1. ProtectedRoute verifica autenticación.
             2. TenantLayout renderiza el Sidebar y el contenedor principal.
             3. Las rutas anidadas se renderizan dentro del Outlet del Layout.
          */}
          <Route
            element={
              <ProtectedRoute>
                <TenantLayout />
              </ProtectedRoute>
            }
          >
            {/* Centro de operación */}
            <Route path="/app/overview" element={<TenantDashboard />} />
            
            {/* Configuración */}
            <Route path="/app/channels" element={<ChannelSetup />} />
            
            {/* Automatización */}
            <Route path="/app/templates" element={<TemplateBlueprintsPage />} />
            <Route path="/app/automations" element={<FlowBuilder />} />
            <Route path="/app/assistant" element={<AiAssistantPage />} />
            
            {/* Mensajería */}
            <Route path="/app/messages" element={<MessagesLog />} />
            <Route path="/app/inbox" element={<AgentInboxPage />} />
            <Route path="/app/contacts" element={<ContactsPage />} />
            <Route path="/app/campaigns" element={<CampaignsPage />} />

            {/* Facturación / suscripción */}
            <Route path="/app/billing" element={<BillingPage />} />
            <Route path="/app/team" element={<TeamPage />} />
            {/* Catálogo de etiquetas: la escritura la limita la RLS a owner/admin; la página se
                muestra en modo lectura para el resto en vez de redirigir. */}
            <Route path="/app/tags" element={<TagsPage />} />

            {/* Perfil de la persona. Sin gate de rol: todos —incluido `viewer`— tienen derecho a
                corregir su propio nombre. La RLS y el GRANT por columna hacen el resto. */}
            <Route path="/app/perfil" element={<ProfilePage />} />
            <Route path="/app/integrations" element={<TenantIntegrationsPage />} />

            {/* Plataforma (solo super-admin; la página redirige si no lo es) */}
            <Route path="/app/admin" element={<AdminPanel />} />
            <Route path="/app/admin/capabilities" element={<AdminCapabilitiesPage />} />
            <Route path="/app/admin/integrations" element={<AdminIntegrationsPage />} />
          </Route>

          {/* Compatibilidad: enlaces guardados y sesiones previas nunca quedan rotos. */}
          {Object.entries(legacyAppRoutes).map(([from, to]) => (
            <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
          ))}
          <Route path="/app" element={<LegacyRedirect to="/app/overview" />} />

          {/* Fallback 404 */}
          <Route path="*" element={<NotFound />} />
          
        </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
